import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, Send, Smartphone, RotateCcw, Check, ChevronDown, User, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { Workflow, AskStep, workflowsApi } from '../../../../services/workflowsApi';
import {
  whatsappApi,
  WhatsAppRecipient,
  SimulatorMessage,
  SimulatorSession,
  SimulatorChoice,
} from '../../../../services/whatsappApi';

/**
 * WhatsApp Simulator — REAL backend round-trip.
 *
 * Calls the workflow engine in simulator_mode (outbound goes to
 * `simulator_messages`, NOT Meta). Inbound replies flow through
 * `engine.handle_inbound()` exactly like production, so observations get
 * written to `recipient_observations` and downstream analytics see the
 * data.
 *
 * Flow:
 *   1. User picks a real recipient → POST /simulate-for → backend starts a
 *      simulator session, fires the first step, returns the thread.
 *   2. User clicks a quick reply / types → POST /simulator-inbound → engine
 *      records observation, advances, next bubble lands in thread.
 *   3. UI re-renders from the thread shape.
 *
 * The synthetic "Demo Patient" option is gone — backend simulation needs a
 * real recipient. If none exist yet, the picker shows a clear empty state.
 */

interface WhatsAppSimulatorProps {
  workflow: Workflow;
  clientId: string;
  businessName?: string;
  onClose: () => void;
}

const NOW_FMT = (iso?: string | null) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
};

export const WhatsAppSimulator: React.FC<WhatsAppSimulatorProps> = ({
  workflow,
  clientId,
  businessName,
  onClose,
}) => {
  const [recipients, setRecipients] = useState<WhatsAppRecipient[]>([]);
  const [recipient, setRecipient] = useState<WhatsAppRecipient | null>(null);
  const [showRecipientMenu, setShowRecipientMenu] = useState(false);

  const [thread, setThread] = useState<SimulatorMessage[]>([]);
  const [session, setSession] = useState<SimulatorSession | null>(null);
  const [textInput, setTextInput] = useState('');
  const [freeText, setFreeText] = useState('');
  const [starting, setStarting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Fetch the recipient roster
  useEffect(() => {
    (async () => {
      try {
        const res = await whatsappApi.listRecipients(clientId, { limit: 100 });
        setRecipients(res.recipients || []);
        // Auto-select the first recipient so the simulator starts immediately.
        if (res.recipients?.length && !recipient) setRecipient(res.recipients[0]);
      } catch (e: any) {
        setError(e?.response?.data?.detail || 'Failed to load recipients');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const startSession = useCallback(
    async (r: WhatsAppRecipient) => {
      setStarting(true);
      setError(null);
      try {
        // Reset any stale simulator state first so we always start clean.
        await whatsappApi.simulatorReset(clientId, r.id).catch(() => {});
        const res = await whatsappApi.simulatorStart(clientId, workflow.id, r.id);
        setSession(res.session || null);
        setThread(res.thread || []);
      } catch (e: any) {
        const msg = e?.response?.data?.detail || e?.message || 'Failed to start simulator';
        setError(msg);
      } finally {
        setStarting(false);
      }
    },
    [clientId, workflow.id],
  );

  // Start a session whenever the active recipient changes.
  useEffect(() => {
    if (!recipient) return;
    startSession(recipient);
  }, [recipient, startSession]);

  // Auto-scroll
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [thread, submitting]);

  const submit = async (rawValue: string) => {
    if (!recipient) return;
    if (!rawValue.trim()) return;
    setSubmitting(true);
    try {
      const res = await whatsappApi.simulatorInbound(
        clientId,
        recipient.id,
        rawValue.trim(),
        session?.id,
      );
      if (res.thread) setThread(res.thread);
      if (res.session) setSession(res.session);
      setTextInput('');
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to submit reply');
    } finally {
      setSubmitting(false);
    }
  };

  const restart = async () => {
    if (!recipient) return;
    setThread([]);
    setSession(null);
    await startSession(recipient);
  };

  // Determine if we're awaiting input by inspecting the LAST outbound message
  // — if it has a response_type, the engine is waiting on the recipient.
  const lastOutbound = useMemo(() => {
    for (let i = thread.length - 1; i >= 0; i--) {
      if (thread[i].direction === 'outbound') return thread[i];
    }
    return null;
  }, [thread]);

  // Derive what the input control should render from the latest outbound
  // bubble. The backend already shaped choices as [{label, image_url?}, ...]
  // so we use that directly. For scale steps the engine doesn't ride min/max
  // on simulator_messages — fall back to the workflow definition.
  const awaiting = useMemo<{
    responseType: 'text' | 'number' | 'choice' | 'scale' | 'emoji';
    choices?: SimulatorChoice[];
    scaleMin?: number;
    scaleMax?: number;
  } | null>(() => {
    if (!lastOutbound || !lastOutbound.response_type) return null;
    const wfStep = (workflow.definition?.steps || []).find(
      (s: any) => s.id === lastOutbound.step_id,
    ) as AskStep | undefined;
    return {
      responseType: lastOutbound.response_type,
      choices: lastOutbound.choices || undefined,
      scaleMin: wfStep?.scale_min,
      scaleMax: wfStep?.scale_max,
    };
  }, [lastOutbound, workflow]);

  const senderName = businessName || workflow.name || 'Your Bot';
  const recipientLabel = recipient?.display_name || recipient?.phone_e164 || 'Pick a recipient';
  const isFinished = !!session && (session.status === 'completed' || session.status === 'errored');

  return (
    <div
      className="fixed bottom-4 right-4 z-50 w-[380px] max-h-[85vh] flex flex-col rounded-2xl shadow-2xl border border-gray-200 overflow-hidden bg-white"
      style={{ boxShadow: '0 20px 50px -12px rgba(0,0,0,0.35)' }}
    >
      {/* Header */}
      <div className="bg-[#075E54] text-white">
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center">
            <Smartphone size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{senderName}</div>
            <div className="text-[11px] opacity-80">
              Simulator · engine sees this · observations persist
            </div>
          </div>
          <button onClick={restart} title="Restart" disabled={starting || !recipient}
            className="p-1.5 rounded-md hover:bg-white/10 text-white/80 hover:text-white disabled:opacity-40">
            <RotateCcw size={14} />
          </button>
          <button onClick={onClose} title="Close"
            className="p-1.5 rounded-md hover:bg-white/10 text-white/80 hover:text-white">
            <X size={14} />
          </button>
        </div>

        {/* Recipient picker */}
        <div className="px-3 pb-2 relative">
          <button
            onClick={() => setShowRecipientMenu((v) => !v)}
            className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-white/10 hover:bg-white/15 text-xs"
          >
            <User size={12} />
            <span className="flex-1 text-left truncate">
              Simulating as: <span className="font-medium">{recipientLabel}</span>
            </span>
            <ChevronDown size={12} className={showRecipientMenu ? 'rotate-180 transition-transform' : 'transition-transform'} />
          </button>
          {showRecipientMenu && (
            <div className="absolute left-3 right-3 top-full mt-1 z-10 bg-white text-gray-900 rounded-lg shadow-lg border border-gray-200 max-h-[220px] overflow-y-auto">
              {recipients.length === 0 ? (
                <div className="px-3 py-3 text-xs text-gray-500 italic">
                  No recipients yet. Add one in the Recipients tab to use the simulator.
                </div>
              ) : (
                recipients.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => {
                      setRecipient(r);
                      setShowRecipientMenu(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 border-b border-gray-100 ${
                      r.id === recipient?.id ? 'bg-emerald-50' : ''
                    }`}
                  >
                    <div className="font-medium">{r.display_name || r.phone_e164}</div>
                    <div className="text-gray-400 truncate">
                      {r.phone_e164} · {r.tags.join(', ') || 'no tags'}
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-3 py-3 space-y-2"
        style={{
          background: 'repeating-linear-gradient(45deg, #ECE5DD 0 12px, #E8DFD3 12px 24px)',
          minHeight: '320px',
          maxHeight: '460px',
        }}
      >
        {!recipient && !starting && (
          <div className="flex flex-col items-center justify-center h-full text-center text-xs text-gray-600 px-6">
            <User size={24} className="text-gray-400 mb-2" />
            <div className="font-medium">Pick a recipient to simulate against</div>
            <div className="text-gray-500 mt-1">
              The simulator runs the real workflow engine — observations land in your reports.
            </div>
          </div>
        )}

        {starting && (
          <div className="flex justify-start">
            <div className="bg-white rounded-lg px-3 py-2 shadow-sm text-xs text-gray-500 inline-flex items-center gap-1.5">
              <Loader2 size={12} className="animate-spin" /> Starting session…
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700 flex items-start gap-2">
            <AlertCircle size={14} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {thread.map((m) => (
          <div key={m.id} className={`flex ${m.direction === 'inbound' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[80%] rounded-lg px-2.5 py-2 shadow-sm text-sm whitespace-pre-wrap leading-relaxed ${
                m.direction === 'inbound' ? 'bg-[#DCF8C6] text-gray-900' : 'bg-white text-gray-900'
              }`}
            >
              {m.image_url && (
                <img
                  src={workflowsApi.previewUrl(m.image_url)}
                  alt=""
                  className="rounded-md max-w-[240px] max-h-[180px] object-cover mb-1"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.opacity = '0.4';
                  }}
                />
              )}
              {m.body && <div>{m.body}</div>}
              {m.step_id && (
                <div className="text-[10px] text-gray-400 mt-1 italic">step {m.step_id}</div>
              )}
              <div className="text-[10px] text-gray-400 mt-1 text-right">{NOW_FMT(m.created_at)}</div>
            </div>
          </div>
        ))}

        {submitting && (
          <div className="flex justify-start">
            <div className="bg-white rounded-lg px-3 py-2 shadow-sm">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input area: structured quick replies (when workflow awaiting) +
          always-visible free-form text input (chat agent picks up anything
          the workflow doesn't consume — RAG over the client's KB). */}
      <div className="border-t border-gray-200 bg-white">
        {!recipient ? (
          <div className="p-3 text-center text-xs text-gray-400">
            Pick a recipient above to begin.
          </div>
        ) : (
          <>
            {isFinished && (
              <div className="px-3 py-1.5 bg-emerald-50 border-b border-emerald-100 text-[11px] text-emerald-700 flex items-center justify-center gap-2">
                <Check size={11} /> Workflow complete — keep chatting freely below
                <button
                  onClick={restart}
                  className="text-emerald-700 hover:text-emerald-900 underline underline-offset-2 ml-1"
                  disabled={starting}
                >
                  Restart
                </button>
              </div>
            )}

            {awaiting && !isFinished && (
              <AwaitingInput
                responseType={awaiting.responseType}
                choices={awaiting.choices}
                scaleMin={awaiting.scaleMin}
                scaleMax={awaiting.scaleMax}
                value={textInput}
                onChangeValue={setTextInput}
                onSubmit={(v) => submit(v)}
                disabled={submitting}
              />
            )}

            {/* Free-form chat input — always visible. Sends through the same
                endpoint; backend routes to chat agent if no workflow ask
                consumes it. */}
            <FreeFormInput
              value={freeText}
              onChangeValue={setFreeText}
              onSubmit={(v) => {
                setFreeText('');
                submit(v);
              }}
              disabled={submitting || starting}
              placeholder={
                awaiting
                  ? 'Or type a question instead…'
                  : 'Ask anything — the bot answers from your knowledge base'
              }
            />
          </>
        )}
      </div>
    </div>
  );
};

const AwaitingInput: React.FC<{
  responseType: 'text' | 'number' | 'choice' | 'scale' | 'emoji';
  choices?: SimulatorChoice[];
  scaleMin?: number;
  scaleMax?: number;
  value: string;
  onChangeValue: (v: string) => void;
  onSubmit: (raw: string) => void;
  disabled?: boolean;
}> = ({ responseType, choices, scaleMin, scaleMax, value, onChangeValue, onSubmit, disabled }) => {
  if (responseType === 'choice' || responseType === 'emoji') {
    const list = choices || [];
    return (
      <div className="p-2.5 space-y-1.5 max-h-[260px] overflow-y-auto">
        {list.length === 0 ? (
          <div className="text-xs text-gray-400 italic px-2">(no choices configured)</div>
        ) : (
          list.map((choice) => (
            <button
              key={choice.label}
              disabled={disabled}
              onClick={() => onSubmit(choice.label)}
              className="w-full flex items-center gap-2.5 text-left px-2.5 py-2 rounded-lg border border-gray-200 text-sm bg-white hover:bg-emerald-50 hover:border-emerald-300 transition-colors disabled:opacity-50"
            >
              {choice.image_url && (
                <img
                  src={workflowsApi.previewUrl(choice.image_url)}
                  alt=""
                  className="w-10 h-10 rounded-md object-cover shrink-0 border border-gray-100"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.opacity = '0.3';
                  }}
                />
              )}
              <span className="flex-1">{choice.label}</span>
            </button>
          ))
        )}
      </div>
    );
  }

  if (responseType === 'scale') {
    const min = scaleMin ?? 1;
    const max = scaleMax ?? 10;
    const range = Array.from({ length: max - min + 1 }, (_, i) => min + i);
    return (
      <div className="p-3">
        <div className="flex gap-1 flex-wrap justify-center">
          {range.map((n) => (
            <button
              key={n}
              disabled={disabled}
              onClick={() => onSubmit(String(n))}
              className="w-10 h-10 rounded-lg border border-gray-200 text-sm font-medium bg-white hover:bg-emerald-50 hover:border-emerald-300 transition-colors disabled:opacity-50"
            >
              {n}
            </button>
          ))}
        </div>
      </div>
    );
  }

  const isNumber = responseType === 'number';
  const handleSubmit = () => {
    if (!value.trim()) return;
    onSubmit(value.trim());
  };
  return (
    <div className="p-3 flex gap-2">
      <input
        type={isNumber ? 'number' : 'text'}
        value={value}
        onChange={(e) => onChangeValue(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
        placeholder={isNumber ? 'Enter a number…' : 'Type your reply…'}
        className="flex-1 px-3 py-2 rounded-full border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
        autoFocus
        disabled={disabled}
      />
      <button
        onClick={handleSubmit}
        disabled={!value.trim() || disabled}
        className="w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center disabled:opacity-40 hover:bg-emerald-700"
      >
        <Send size={16} />
      </button>
    </div>
  );
};

const FreeFormInput: React.FC<{
  value: string;
  onChangeValue: (v: string) => void;
  onSubmit: (raw: string) => void;
  disabled?: boolean;
  placeholder: string;
}> = ({ value, onChangeValue, onSubmit, disabled, placeholder }) => {
  const handleSubmit = () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    onSubmit(trimmed);
  };
  return (
    <div className="p-2.5 flex gap-2 border-t border-gray-100">
      <input
        type="text"
        value={value}
        onChange={(e) => onChangeValue(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
        placeholder={placeholder}
        className="flex-1 px-3 py-2 rounded-full border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
        disabled={disabled}
      />
      <button
        onClick={handleSubmit}
        disabled={!value.trim() || disabled}
        className="w-9 h-9 rounded-full bg-emerald-600 text-white flex items-center justify-center disabled:opacity-40 hover:bg-emerald-700"
      >
        <Send size={14} />
      </button>
    </div>
  );
};

export default WhatsAppSimulator;
